import { ref, shallowRef, toValue, watch, onBeforeUnmount, type MaybeRefOrGetter } from 'vue';
import qs from 'qs';
import { useDebounceFn } from '@vueuse/core';
import type { MetricData } from '@/components/metrics/metrics';

export interface MetricQuery {
  filters?: Record<string, unknown>
  interval?: string
  limit?: number
}

export function metricUrl(collection: string, metric: string): string {
  return `${window.Laravel?.backofficeApi}/${collection}/metrics/${metric}`;
}

/**
 * The query string a metric is read with: empty filters are left out, so a
 * cleared field reads as no filter rather than as a filter on nothing.
 */
export function metricParams(query: MetricQuery): Record<string, unknown> {
  const filter = Object.fromEntries(
    Object.entries(query.filters ?? {}).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );

  return {
    filter,
    ...(query.interval ? { interval: query.interval } : {}),
    ...(query.limit ? { limit: query.limit } : {}),
  };
}

/**
 * Reads one metric and reads it again whenever its query changes.
 *
 * A reading still in flight when the query moves on is cancelled at once,
 * not when the debounced reading starts, so a slow answer to an old question
 * can never land over the answer to the new one. Once the card is gone, no
 * reading starts any more.
 *
 * The query is watched by value: a board hands down a fresh object on every
 * change, which would otherwise read the metric again for nothing.
 */
export function useMetric(
  collection: MaybeRefOrGetter<string>,
  metric: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<MetricQuery> = {},
) {
  const data = shallowRef<MetricData | null>(null);
  const loading = ref(false);
  const error = ref<unknown>(null);

  let controller: AbortController | null = null;
  let unmounted = false;

  /** Cancels the reading in flight, which then leaves the loading state alone. */
  function cancel(): void {
    controller?.abort();
    controller = null;
  }

  async function load(): Promise<void> {
    if (unmounted) {
      return;
    }

    cancel();
    const current = new AbortController();
    controller = current;

    loading.value = true;
    error.value = null;

    try {
      const url = `${metricUrl(toValue(collection), toValue(metric))}?${qs.stringify(metricParams(toValue(query)))}`;
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: current.signal,
      });

      if (!response.ok) {
        throw new Error(`The metric could not be read (${response.status}).`);
      }

      const { data: reading } = await response.json();

      if (!current.signal.aborted) {
        data.value = reading;
      }
    } catch (exception) {
      if (current.signal.aborted) {
        return;
      }

      error.value = exception;
    } finally {
      if (controller === current) {
        loading.value = false;
      }
    }
  }

  const debouncedLoad = useDebounceFn(load, 200);

  watch(
    () => JSON.stringify([toValue(collection), toValue(metric), metricParams(toValue(query))]),
    () => {
      cancel();
      loading.value = true;
      debouncedLoad();
    },
  );

  onBeforeUnmount(() => {
    unmounted = true;
    cancel();
  });

  void load();

  return {
    data,
    loading,
    error,
    load,
  };
}
